/**
 * InlineDdlSequenceBody — the embedded content for the sequence editor
 * (`CREATE SEQUENCE`), mounted directly (no trigger, no Popover chrome) for
 * the Compact VQE popover's `compactDdlBodyArm`.
 *
 * Seeds the form by running a lightweight lexical scan over the parked
 * statement text (`parseParkedSequenceStatement`) — DSE2 does not yet expose
 * a structured sequence arm in `extractDdlStructure`, so this path does its
 * own scan.
 *
 * Mode: derived from the parked statement's own verb (`parsed.action`) so a
 * parked `CREATE SEQUENCE` gets create semantics (create title/CTA, editable
 * name + data type, "Start with" field) and dispatches `commitCreateSequence`,
 * while `ALTER SEQUENCE` keeps alter semantics. Mirrors every sibling inline
 * body (database / routine / schema / trigger / security / package).
 *
 * Engine gating: mysql + sqlite have no CREATE / ALTER SEQUENCE — renders a
 * muted one-line notice pointing at AUTO_INCREMENT / AUTOINCREMENT
 * respectively. The gate follows the derived mode so the notice matches the
 * operation the user actually parked.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D3 — Sequence UI.
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T4a/T5).
 */
import { useMemo } from 'react';

import type { QualifiedName } from '../../../sql-grammar/model';
import {
  getAlterSequenceUnsupportedTooltip,
  isAlterSequenceSupported,
} from '../edit/edits/alterSequence';
import {
  getCreateSequenceUnsupportedTooltip,
  isCreateSequenceSupported,
} from '../edit/edits/createSequence';
import type { VqeDdlEditContext } from '../edit/editContext';
import { SequenceEditFormBody } from '../render/common/SequenceEditFormBody';
import type { SequenceFormValues } from '../render/common/sequenceFormHelpers';
import {
  parseParkedSequenceStatement,
  type ParsedParkedSequence,
} from './parseParkedSequenceStatement';

const FALLBACK_SEQUENCE_NAME: QualifiedName = {
  name: '',
  raw: '',
  quoted: false,
  foldedName: '',
};

function valuesFromParsed(parsed: ParsedParkedSequence): SequenceFormValues {
  return {
    name: parsed.name.name,
    dataType: parsed.dataType ?? '',
    startWith: parsed.startWith ?? '',
    incrementBy: parsed.incrementBy ?? '',
    minValue: parsed.minValue ?? '',
    maxValue: parsed.maxValue ?? '',
    cycle: parsed.cycle ?? '',
    cache: parsed.cache ?? '',
  };
}

function useParsedParkedSequence(
  editContext: VqeDdlEditContext,
  parsedOverride: ParsedParkedSequence | null | undefined,
): ParsedParkedSequence | null {
  return useMemo<ParsedParkedSequence | null>(() => {
    if (parsedOverride !== undefined) return parsedOverride;
    try {
      const buf = editContext.service.getParsedBuffer(editContext.documentId);
      if (!buf) return null;
      const stmt = buf.documentStructure.statements.find(
        (s) => s.id === editContext.statementId,
      );
      if (!stmt) return null;
      const text = buf.parsedDocument.source.slice(
        stmt.range.startOffset,
        stmt.range.endOffset,
      );
      return parseParkedSequenceStatement(text);
    } catch {
      return null;
    }
  }, [parsedOverride, editContext.service, editContext.documentId, editContext.statementId]);
}

export interface InlineDdlSequenceBodyProps {
  readonly editContext: VqeDdlEditContext;
  /** Test seam — overrides the parsed parked-sequence (skips the scan). */
  readonly parsedOverride?: ParsedParkedSequence | null;
  /** Called after a successful commit (or Cancel) so the host can react. */
  readonly onDone?: () => void;
  /**
   * Cancel-only escape, when the host wants Cancel and commit to mean
   * DIFFERENT things. Falls back to `onDone` when omitted, so every existing
   * caller keeps the old "commit or cancel both call `onDone`" behaviour.
   * The compact popover passes it to close on Cancel WITHOUT closing on a
   * successful commit (the commit funnels through the app-shell
   * PreviewDialog, which needs this body’s context to stay mounted).
   */
  readonly onCancel?: () => void;
}

/**
 * Embedded body for the Compact VQE popover's `compactDdlBodyArm`, mounted
 * directly with no trigger/Popover chrome.
 */
export function InlineDdlSequenceBody({
  editContext,
  parsedOverride,
  onDone,
  onCancel,
}: InlineDdlSequenceBodyProps) {
  const engine = editContext.service.engineId;
  const parsed = useParsedParkedSequence(editContext, parsedOverride);

  const sequenceName = parsed?.name ?? FALLBACK_SEQUENCE_NAME;
  const initialValues = parsed ? valuesFromParsed(parsed) : undefined;
  const mode: 'create' | 'alter' = parsed?.action === 'alter' ? 'alter' : 'create';

  const supported =
    mode === 'create' ? isCreateSequenceSupported(engine) : isAlterSequenceSupported(engine);
  if (!supported) {
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid="inline-ddl-sequence-unsupported"
      >
        {mode === 'create'
          ? getCreateSequenceUnsupportedTooltip(engine)
          : getAlterSequenceUnsupportedTooltip(engine)}
      </p>
    );
  }

  return (
    <SequenceEditFormBody
      editContext={editContext}
      mode={mode}
      sequenceName={sequenceName}
      initialValues={initialValues}
      testId="inline-ddl-sequence-form"
      onCommitted={() => onDone?.()}
      onCancel={() => (onCancel ?? onDone)?.()}
    />
  );
}
